'use client';

import React from 'react';
import { auth } from '@/lib/firebase';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';

/**
 * The meet sign-in surface, shared by the landing page and the room page.
 *
 * All four paths converge on one thing — the HttpOnly meet session cookie set by
 * the /api/auth/* routes — so the rest of the app only ever asks "is there a
 * session", never "how did they sign in". See lib/meetSession.ts.
 *
 *   password → Firebase sign-in, then POST the ID token to /api/auth/session
 *   google   → GIS credential → /api/auth/google (server maps email → account)
 *   device   → short code approved in the CA Kedia app → /api/auth/device/poll
 *   sso      → handled on the landing page from the ?sso= hand-off
 */

export type Role = 'admin' | 'team' | 'client';
export type Session = { uid: string; name: string; role: Role; via?: string };

export const roleLabel: Record<Role, string> = {
  admin: 'Admin',
  team: 'Team',
  client: 'Client',
};
export const roleColor: Record<Role, string> = {
  admin: '#1565C0',
  team: '#0f9d58',
  client: '#6b7280',
};

/** The public web portal's signup form — where "Request access" sends people. */
const SIGNUP_URL = 'https://aayush-kedia-and-company.web.app/?signup=1';

const GOOGLE_CLIENT_ID = process.env.NEXT_PUBLIC_GOOGLE_OAUTH_CLIENT_ID ?? '';

/** Resolves the current session from the cookie. `null` = guest. */
export function useMeetSession() {
  const [session, setSession] = React.useState<Session | null>(null);
  const [loading, setLoading] = React.useState(true);

  const refresh = React.useCallback(async () => {
    try {
      const r = await fetch('/api/auth/session', { cache: 'no-store' });
      const d = await r.json();
      setSession(d?.session ?? null);
    } catch {
      setSession(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  const logout = React.useCallback(async () => {
    // Both halves: the cookie is what the server trusts, but a Firebase session
    // left behind would silently re-establish it on the next password path.
    await fetch('/api/auth/session', { method: 'DELETE' }).catch(() => {});
    await signOut(auth).catch(() => {});
    setSession(null);
  }, []);

  return { session, setSession, loading, refresh, logout };
}

// ── Google Identity Services ────────────────────────────────────────────────
// Loaded on demand rather than in the document head: most visitors are already
// signed in or joining as guests and never need it.
let gisPromise: Promise<void> | null = null;
function loadGis(): Promise<void> {
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') return reject(new Error('ssr'));
    if ((window as any).google?.accounts?.id) return resolve();
    const s = document.createElement('script');
    s.src = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('gis-load-failed'));
    document.head.appendChild(s);
  });
  return gisPromise;
}

type UnknownEmail = { email: string };

export function SignInPanel(props: {
  styles: Record<string, string>;
  onSignedIn: (s: Session) => void;
  /** Room pages offer a guest path; the landing page does not. */
  onContinueAsGuest?: () => void;
  heading?: string;
  subheading?: string;
}) {
  const { styles } = props;
  const [email, setEmail] = React.useState('');
  const [pass, setPass] = React.useState('');
  const [err, setErr] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [unknown, setUnknown] = React.useState<UnknownEmail | null>(null);
  const [device, setDevice] = React.useState<{ code: string; secret: string } | null>(null);
  const [mode, setMode] = React.useState<'choice' | 'password'>('choice');

  const finish = (name: string, role: Role) => props.onSignedIn({ uid: '', name, role });

  // ── Password ──────────────────────────────────────────────────────────────
  const loginPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), pass);
      const token = await cred.user.getIdToken();
      const r = await fetch('/api/auth/session', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      if (!r.ok) throw new Error('session');
      const d = await r.json();
      finish(d.name, d.role);
    } catch (e: any) {
      // A valid Firebase login that the server refuses means a deleted or
      // suspended account — do not leave a half-signed-in Firebase session lying
      // around, or the UI would disagree with the server about who is here.
      await signOut(auth).catch(() => {});
      setErr(
        e?.message === 'session'
          ? 'This account cannot join meetings. Contact the CA Kedia team.'
          : 'Incorrect email or password.',
      );
    } finally {
      setBusy(false);
    }
  };

  // ── Google ────────────────────────────────────────────────────────────────
  const loginGoogle = React.useCallback(
    async (forceChooser: boolean) => {
      setErr('');
      setUnknown(null);
      if (!GOOGLE_CLIENT_ID) {
        setErr('Google sign-in is not configured.');
        return;
      }
      setBusy(true);
      try {
        await loadGis();
        const google = (window as any).google;
        const credential: string = await new Promise((resolve, reject) => {
          google.accounts.id.initialize({
            client_id: GOOGLE_CLIENT_ID,
            callback: (resp: any) =>
              resp?.credential ? resolve(resp.credential) : reject(new Error('cancelled')),
            // "Use your registered email" has to be able to get PAST a
            // remembered account, otherwise it re-submits the same address that
            // just failed and the user is stuck in a loop.
            auto_select: false,
            ...(forceChooser ? { prompt_parent_id: undefined } : {}),
          });
          google.accounts.id.prompt((n: any) => {
            if (n?.isNotDisplayed?.() || n?.isSkippedMoment?.()) reject(new Error('cancelled'));
          });
        });

        const r = await fetch('/api/auth/google', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ credential }),
        });
        const d = await r.json();
        if (d?.status === 'ok') {
          finish(d.name, d.role);
          return;
        }
        if (d?.status === 'unknown') {
          setUnknown({ email: d.email });
          return;
        }
        setErr('Could not sign in with Google.');
      } catch (e: any) {
        if (e?.message !== 'cancelled') setErr('Could not sign in with Google.');
      } finally {
        setBusy(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // ── Approve in the app ────────────────────────────────────────────────────
  const startDevice = async () => {
    setErr('');
    setBusy(true);
    try {
      const r = await fetch('/api/auth/device/start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ label: navigator.userAgent.slice(0, 100) }),
      });
      const d = await r.json();
      if (!d?.code) throw new Error();
      setDevice({ code: d.code, secret: d.secret });
    } catch {
      setErr('Could not start app sign-in. Try again.');
    } finally {
      setBusy(false);
    }
  };

  React.useEffect(() => {
    if (!device) return;
    let stopped = false;
    const timer = setInterval(async () => {
      try {
        const r = await fetch('/api/auth/device/poll', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(device),
        });
        const d = await r.json();
        if (stopped) return;
        if (d?.status === 'ok') {
          clearInterval(timer);
          setDevice(null);
          finish(d.name, d.role);
        } else if (d?.status === 'expired' || d?.status === 'denied') {
          clearInterval(timer);
          setDevice(null);
          setErr(
            d.status === 'expired'
              ? 'That code expired. Please try again.'
              : 'That request was not approved.',
          );
        }
      } catch {
        /* transient — keep polling */
      }
    }, 2000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [device]);

  // ── Unknown Gmail ─────────────────────────────────────────────────────────
  if (unknown) {
    return (
      <div className={`${styles.card} ${styles.login}`}>
        <h1>No account for that email</h1>
        <p className={styles.muted}>
          <strong>{unknown.email}</strong> isn’t registered with CA Kedia.
        </p>
        <p className={`${styles.tiny} ${styles.muted}`}>
          If you already have an account, sign in with the email address the firm has on
          record — it may be a different Google account.
        </p>
        <button className={styles.primary} onClick={() => loginGoogle(true)} disabled={busy}>
          Use my registered email
        </button>
        <button className={styles.link} onClick={() => (window.location.href = SIGNUP_URL)}>
          Request access
        </button>
        <button className={styles.link} onClick={() => setUnknown(null)}>
          Back
        </button>
        {props.onContinueAsGuest && (
          <button className={styles.link} onClick={props.onContinueAsGuest}>
            Join as a guest instead
          </button>
        )}
      </div>
    );
  }

  // ── Waiting for app approval ──────────────────────────────────────────────
  if (device) {
    return (
      <div className={`${styles.card} ${styles.login}`}>
        <h1>Approve in the app</h1>
        <p className={styles.muted}>
          Open the CA Kedia app and enter this code under <strong>Meetings → Sign in a
          browser</strong>.
        </p>
        <div
          style={{
            fontSize: 34,
            fontWeight: 800,
            letterSpacing: 4,
            margin: '18px 0',
            fontFamily: 'ui-monospace, monospace',
          }}
        >
          {device.code}
        </div>
        <p className={`${styles.tiny} ${styles.muted}`}>
          This code expires in 3 minutes. Only approve it if you started this sign-in.
        </p>
        <button className={styles.link} onClick={() => setDevice(null)}>
          Cancel
        </button>
      </div>
    );
  }

  // ── Password form ─────────────────────────────────────────────────────────
  if (mode === 'password') {
    return (
      <form className={`${styles.card} ${styles.login}`} onSubmit={loginPassword}>
        <h1>Sign in</h1>
        <p className={styles.muted}>Use your CA Kedia app login</p>
        <input
          className={styles.input}
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <input
          className={styles.input}
          type="password"
          placeholder="Password"
          value={pass}
          onChange={(e) => setPass(e.target.value)}
          required
        />
        {err && <div className={styles.err}>{err}</div>}
        <button className={styles.primary} type="submit" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <button className={styles.link} type="button" onClick={() => setMode('choice')}>
          Back
        </button>
      </form>
    );
  }

  // ── Method chooser ────────────────────────────────────────────────────────
  return (
    <div className={`${styles.card} ${styles.login}`}>
      <h1>{props.heading ?? 'Sign in'}</h1>
      <p className={styles.muted}>{props.subheading ?? 'Use your CA Kedia account'}</p>
      {err && <div className={styles.err}>{err}</div>}
      <button className={styles.primary} onClick={() => loginGoogle(false)} disabled={busy}>
        Continue with Google
      </button>
      <button className={styles.secondary} onClick={startDevice} disabled={busy}>
        Approve in the CA Kedia app
      </button>
      <button className={styles.secondary} onClick={() => setMode('password')} disabled={busy}>
        Use email and password
      </button>
      {props.onContinueAsGuest && (
        <button className={styles.link} onClick={props.onContinueAsGuest}>
          Join as a guest
        </button>
      )}
      <p className={`${styles.tiny} ${styles.muted}`}>
        Opened from the CA Kedia app? You are signed in automatically.
      </p>
    </div>
  );
}
